import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Users, Save } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ScoutSettingsPanel = ({ getAuthHeaders }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [rewardPct, setRewardPct] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [enabled, setEnabled] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/scout-settings`, { headers });
      const s = res.data.settings;
      setSettings(s);
      setThreshold(String(s.minScoutOutboundTipTotal));
      setRewardPct(String(s.scoutRewardPct));
      setDurationDays(String(s.affiliationDurationDays));
      setEnabled(s.scoutEligibilityEnabled);
    } catch (err) {
      toast.error('Failed to load scout settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    const parsedThreshold = parseFloat(threshold);
    const parsedRewardPct = parseFloat(rewardPct);
    const parsedDuration = parseInt(durationDays, 10);

    if (isNaN(parsedThreshold) || parsedThreshold < 0) {
      toast.error('Threshold must be a number >= 0');
      return;
    }
    if (isNaN(parsedRewardPct) || parsedRewardPct < 0 || parsedRewardPct > 100) {
      toast.error('Reward % must be between 0 and 100');
      return;
    }
    if (isNaN(parsedDuration) || parsedDuration < 1) {
      toast.error('Duration must be at least 1 day');
      return;
    }

    try {
      setSaving(true);
      const headers = getAuthHeaders();
      const res = await axios.put(`${BACKEND_URL}/api/admin/scout-settings`, {
        scoutEligibilityEnabled: enabled,
        minScoutOutboundTipTotal: parsedThreshold,
        scoutRewardPct: parsedRewardPct,
        affiliationDurationDays: parsedDuration,
      }, { headers });

      setSettings(res.data.settings);
      toast.success(`Scout settings saved (updated: ${res.data.updated_fields.join(', ')})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save scout settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-white border-[#b8b3ab]">
        <div className="flex items-center gap-2 text-[#666]">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading scout settings...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-white border-[#b8b3ab]" data-testid="scout-settings-panel">
      <h2 className="text-lg font-bold text-[#1a1a1a] mb-2 flex items-center gap-2">
        <Users className="w-5 h-5 text-[#f0b90b]" />
        Scout Program Settings
      </h2>
      <p className="text-[#666] text-sm mb-5">
        Configure scout eligibility thresholds, reward percentages, and affiliation duration.
      </p>

      {/* Enable/Disable toggle */}
      <div className="flex items-center justify-between mb-5 p-3 bg-[#f5f3f0] rounded-lg">
        <div>
          <p className="text-sm font-medium text-[#1a1a1a]">Scout Program</p>
          <p className="text-xs text-[#888]">
            {enabled ? 'Wallets meeting the threshold can generate referral links' : 'No wallets can generate referral links'}
          </p>
        </div>
        <button
          onClick={() => setEnabled(!enabled)}
          data-testid="scout-enabled-toggle"
          className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#f0b90b]' : 'bg-[#ccc]'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${enabled ? 'translate-x-6' : ''}`} />
        </button>
      </div>

      {/* Threshold */}
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-[#1a1a1a] block mb-1">
            Minimum total tipped required to generate referral link
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="flex-1 bg-[#d4cfc4] border-[#b8b3ab]"
              data-testid="scout-threshold-input"
            />
            <span className="text-sm font-medium text-[#888] w-10">
              {settings?.scoutThresholdUnit || 'TIP'}
            </span>
          </div>
          <p className="text-xs text-[#888] mt-1">
            Cumulative outbound TIP volume a wallet must have sent before it can generate a scout referral link. Set to 0 to allow all wallets.
          </p>
        </div>

        {/* Reward % */}
        <div>
          <label className="text-sm font-medium text-[#1a1a1a] block mb-1">
            Scout Reward Percentage
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={rewardPct}
              onChange={(e) => setRewardPct(e.target.value)}
              className="flex-1 bg-[#d4cfc4] border-[#b8b3ab]"
              data-testid="scout-reward-pct-input"
            />
            <span className="text-sm font-medium text-[#888] w-10">%</span>
          </div>
          <p className="text-xs text-[#888] mt-1">
            Percentage of affiliated creator's TRUE points carved out to the scout each epoch.
          </p>
        </div>

        {/* Duration */}
        <div>
          <label className="text-sm font-medium text-[#1a1a1a] block mb-1">
            Affiliation Duration
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="1"
              min="1"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              className="flex-1 bg-[#d4cfc4] border-[#b8b3ab]"
              data-testid="scout-duration-input"
            />
            <span className="text-sm font-medium text-[#888] w-16">days</span>
          </div>
          <p className="text-xs text-[#888] mt-1">
            How long a scout earns rewards from an affiliated creator after approval.
          </p>
        </div>
      </div>

      {/* Current values summary */}
      {settings && (
        <Alert className="mt-4 bg-[#f5f3f0] border-[#b8b3ab]">
          <AlertDescription className="text-xs text-[#666]">
            Currently stored: threshold = <strong>{settings.minScoutOutboundTipTotal} {settings.scoutThresholdUnit}</strong>,
            reward = <strong>{settings.scoutRewardPct}%</strong>,
            duration = <strong>{settings.affiliationDurationDays} days</strong>,
            basis = <strong>{settings.scoutRewardBasis}</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Save */}
      <div className="mt-5">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white px-6"
          data-testid="scout-settings-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Scout Settings
        </Button>
      </div>
    </Card>
  );
};
